/**
 * Centralized Wedding Events Data
 * 
 * This file contains all wedding event information used across different pages.
 * Each event has a unique ID and can be filtered by category for different pages.
 * 
 * Categories:
 * - 'pre-wedding': Events before the main wedding day (Mehendi, Haldi, Sangeet)
 * - 'wedding': Main wedding ceremony events
 * - 'reception': Reception and post-wedding events
 * - 'main-agenda': Key events for the main agenda page
 */

// Centralized venue information
export const VENUE_INFO = {
  name: 'The Orchid Hotel Pune',
  address: [
    'Shree Shiv Chhatrapati Sports Complex',
    'Bengaluru - Mumbai Hwy, adjacent Chatrapati Shivaji Maharaj Road',
    'National Games Park, Balewadi',
    'Pune, Maharashtra 411045, India'
  ]
};

export const WEDDING_EVENTS = {
  // Pre-wedding Events
  mehendi: {
    id: 'mehendi',
    category: 'pre-wedding',
    title: 'Mehendi',
    date: 'Saturday, 22nd November 2025',
    time: '4:00 PM – 8:00 PM',
    venue: '[Mehendi Venue Name & Address]',
    attire: 'Bright Colors / Traditional Indian',
    description: 'Join us for the beautiful mehendi ceremony where intricate henna designs will be applied to the bride\'s hands and feet. A joyful celebration filled with music, laughter, and traditional rituals that symbolize love and new beginnings.',
    image: '/mehendi.png',
    flower: '/jasmine.png',
  },

  haldi: {
    id: 'haldi',
    category: 'pre-wedding',
    title: 'Haldi',
    date: 'Monday, 24th November 2025',
    time: '4:30 PM – 6:00 PM',
    venue: '[Haldi Venue Name & Address]',
    attire: 'Yellow / Bright Colors',
    description: 'Experience the vibrant haldi ceremony where turmeric paste is applied to the bride and groom for purification and blessing. This colorful ritual brings good luck and is filled with joy, music, and festive celebrations.',
    image: '/haldi.png',
    flower: '/marigold.png',
  },

  sangeet: {
    id: 'sangeet',
    category: ['pre-wedding', 'sangeet'],
    title: 'Sangeet Night',
    date: 'Monday, 24th November 2025',
    time: '7:00 PM – 9:00 PM',
    venue: '[Sangeet Venue Name & Address]',
    attire: 'Glamorous Ethnic / Indo-Western',
    description: 'Let the music play! Join us for an unforgettable evening filled with energetic dance performances, foot-tapping beats, and joyous laughter as both families come together to celebrate love and togetherness.',
    image: '/sangeet.png',
    flower: '/marigold.png',
  },

  sangeetDinner: {
    id: 'sangeetDinner',
    category: ['pre-wedding', 'sangeet'],
    title: 'Sangeet Dinner',
    date: 'Monday, 24th November 2025',
    time: '9:00 PM onwards',
    venue: '[Sangeet Venue Name & Address]',
    attire: 'Glamorous Ethnic / Indo-Western',
    description: 'Continue the celebration with a delicious dinner featuring traditional and contemporary cuisine. Enjoy great food and conversations as we celebrate the upcoming union.',
    image: '/dinner.png',
    flower: '/orchid.png',
  },

  // Wedding Day Events
  breakfast: {
    id: 'breakfast',
    category: 'wedding',
    title: 'Wedding Breakfast',
    date: 'Tuesday, 25th November 2025',
    time: '8:00 AM – 10:00 AM',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Comfortable Traditional',
    description: 'Start the auspicious wedding day with a hearty breakfast. Join the families for a warm meal before the main ceremonies begin.',
    image: '/breakfast.png',
    flower: '/jasmine.png',
  },

  baraat: {
    id: 'baraat',
    category: 'wedding',
    title: 'Baraat (Groom\'s Procession)',
    date: 'Tuesday, 25th November 2025',
    time: '10:00 AM – 10:30 AM',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Traditional Indian Festive Wear',
    description: 'Witness the grand arrival of the groom with his family and friends in a joyous procession filled with music, dancing, and celebration.',
    image: '/baraat.png',
    flower: '/marigold.png',
  },

  weddingCeremony: {
    id: 'weddingCeremony',
    category: ['wedding', 'main-agenda'],
    title: 'Phere (Wedding Vows)',
    date: 'Tuesday, 25th November 2025',
    time: '10:30 AM – 12:30 PM',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Traditional Indian Festive Wear',
    description: 'Be part of the sacred wedding ceremony as Radhika and Vivek take their vows. Experience the beautiful rituals, mantras, and blessings that unite two souls in matrimony.',
    image: '/wedding_pheras.png',
    flower: '/jasmine.png',
  },

  varmala: {
    id: 'varmala',
    category: ['wedding', 'main-agenda'],
    title: 'Varmala (Garland Exchange)',
    date: 'Tuesday, 25th November 2025',
    time: '12:30 PM – 1:00 PM',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Traditional Indian Festive Wear',
    description: 'Witness the beautiful varmala ceremony where the bride and groom exchange garlands, symbolizing their acceptance of each other as life partners. A joyous moment filled with cheers and blessings from family and friends.',
    image: '/varmala.png',
    flower: '/jasmine.png',
  },

  lunch: {
    id: 'lunch',
    category: ['wedding', 'main-agenda'],
    title: 'Wedding Lunch',
    date: 'Tuesday, 25th November 2025',
    time: '1:00 PM – 3:00 PM',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Traditional Indian Festive Wear',
    description: 'Enjoy a sumptuous lunch featuring traditional Indian cuisine prepared with love. A perfect time to relax and celebrate with family and friends.',
    image: '/lunch.png',
    flower: '/aboli.png',
  },

  // Reception Events
  receptionGathering: {
    id: 'receptionGathering',
    category: 'reception',
    title: 'Reception Gathering',
    date: 'Tuesday, 25th November 2025',
    time: '6:00 PM – 7:30 PM',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Traditional Indian Festive Wear',
    description: 'An elegant evening to meet the newlyweds, extend your blessings, and enjoy warm conversations in a festive atmosphere. Expect soft music, glowing lights, and plenty of happy memories being made.',
    image: '/reception.png',
    flower: '/marigold.png',
  },

  receptionDinner: {
    id: 'receptionDinner',
    category: 'reception',
    title: 'Reception Dinner',
    date: 'Tuesday, 25th November 2025',
    time: '8:00 PM onwards',
    venue: '[Wedding Venue Name & Address]',
    attire: 'Traditional Indian Festive Wear',
    description: 'Conclude the celebrations with an exquisite dinner buffet featuring a variety of cuisines. Toast to the newlyweds and create lasting memories.',
    image: '/reception_dinner.png',
    flower: '/gerbera.png',
  },
};

/**
 * Utility functions to filter events by category
 */
export const getEventsByCategory = (category) => {
  return Object.values(WEDDING_EVENTS).filter(event => {
    if (Array.isArray(event.category)) {
      return event.category.includes(category);
    }
    return event.category === category;
  });
};

export const getEventById = (id) => {
  return WEDDING_EVENTS[id];
};

export const getAllEvents = () => {
  return Object.values(WEDDING_EVENTS);
};

/**
 * Page-specific event collections
 */
export const PAGE_EVENTS = {
  home: () => getAllEvents(), // Show all events on home page
  mainAgenda: () => getEventsByCategory('main-agenda'),
  wedding: () => getEventsByCategory('wedding'),
  reception: () => getEventsByCategory('reception'),
  sangeetNWedding: () => [...getEventsByCategory('sangeet'), ...getEventsByCategory('wedding')],
};